import type { DaySummary } from '@/types/reveal';

/**
 * Can the player still change an EXISTING pick on this day?
 *
 * 🔒 Gated on `pickCutoffAt` ONLY — never on `day.status`.
 *
 * `status` describes the REVEAL lifecycle: a day flips `open -> locked` when its reveal
 * window closes, which — since reveal close became independent of the pick cutoff — can
 * happen long before the pick deadline. Gating this on status is exactly what made an
 * earlier day's pick unreachable in the app while it was still editable (9 Sep 2026:
 * Day 1's reveal-close moved into the past, `POST /reveal` correctly 409'd, but
 * `PUT /pick` still returned 200 — the permission existed and nothing in the UI asked
 * for it). `DaySummary.revealCloseAt` is display-only for the same reason.
 *
 * The SERVER stays the authority. The per-card DKPLAT-212 rule — the selected card's own
 * fixture having started — is *stricter* than this day-level cutoff, so a pick can still
 * be refused when this returns true. That refusal is surfaced by the reveal/pick path's
 * own error handling; it is not replicated here, because fixtures are only loaded for the
 * day currently being viewed.
 *
 * A null `pickCutoffAt` means "unknown deadline", so it hides the affordance rather than
 * offering an edit the server may reject.
 */
export function canEditDaySelection(day: DaySummary, nowMs: number = Date.now()): boolean {
  const entry = day.userEntry;
  if (!entry?.selectedCard) return false;
  if (entry.status === 'graded') return false;
  if (!day.pickCutoffAt) return false;
  const cutoffMs = new Date(day.pickCutoffAt).getTime();
  if (!Number.isFinite(cutoffMs)) return false;
  return cutoffMs > nowMs;
}
